import { RefObject, useLayoutEffect } from 'react';

type ScrollLock = {
    count: number;
    overflow: string;
    overflowPriority: string;
    paddingRight: string;
    paddingPriority: string;
};

// Reference counts keep the page locked when overlays are nested.
const locks = new Map<HTMLElement, ScrollLock>();

function lockScroll(element: HTMLElement, { compensateScrollbar = false, hideOverflow = true } = {}) {
    const existing = locks.get(element);
    if (existing) {
        existing.count += 1;
    } else {
        const scrollbarWidth = compensateScrollbar
            ? Math.max(0, window.innerWidth - document.documentElement.clientWidth)
            : 0;
        const padding = window.getComputedStyle(element).paddingRight;
        locks.set(element, {
            count: 1,
            overflow: element.style.getPropertyValue('overflow'),
            overflowPriority: element.style.getPropertyPriority('overflow'),
            paddingRight: element.style.getPropertyValue('padding-right'),
            paddingPriority: element.style.getPropertyPriority('padding-right'),
        });
        if (hideOverflow) {
            element.style.setProperty('overflow', 'hidden', 'important');
        }
        if (scrollbarWidth > 0) {
            element.style.setProperty('padding-right', `${parseFloat(padding || '0') + scrollbarWidth}px`);
        }
    }

    return () => {
        const lock = locks.get(element);
        if (!lock || --lock.count > 0) return;
        if (lock.overflow) element.style.setProperty('overflow', lock.overflow, lock.overflowPriority);
        else element.style.removeProperty('overflow');
        if (lock.paddingRight) element.style.setProperty('padding-right', lock.paddingRight, lock.paddingPriority);
        else element.style.removeProperty('padding-right');
        locks.delete(element);
    };
}

export function useOverlayScrollLock(scrollRef: RefObject<HTMLDivElement>, enabled = true) {
    useLayoutEffect(() => {
        if (!enabled || !scrollRef.current) return;
        const activeScroll = scrollRef.current;
        // Measure the scrollbar before hiding it. The body only gets the compensation: with overflow
        // hidden on both html and body, the body becomes the scroller and the page jumps to the top.
        const releaseBody = lockScroll(document.body, { compensateScrollbar: true, hideOverflow: false });
        const releaseDocument = lockScroll(document.documentElement);
        const releases = Array.from(document.querySelectorAll<HTMLElement>('[data-page-scroll], [data-overlay-scroll]'))
            .filter((element) => element !== activeScroll && !activeScroll.contains(element))
            .map((element) => lockScroll(element));
        return () => {
            releases.forEach((release) => release());
            releaseDocument();
            releaseBody();
        };
    }, [enabled, scrollRef]);
}
